import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBorrowCart } from "../context/BorrowCartContext";

import { API_BASE_URL } from "../utils/config";

export default function Cart() {
  const navigate = useNavigate();
  const { items, setQuantity, removeItem, clear, header, setHeaderField } = useBorrowCart();
  const [borrowDate, setBorrowDate] = useState("");
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [purposeNotes, setPurposeNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const todayISO = useMemo(
    () => new Date().toISOString().split("T")[0],
    []
  );

  // image preview modal (zoom)
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const token = localStorage.getItem("accessToken");

  // Use the SAME idea as ItemsList: proxy Google Drive images via backend
  function normalizeImageUrl(url) {
    if (!url) return null;
    const trimmed = String(url).trim();
    if (!trimmed) return null;

    const extractDriveId = (value) => {
      if (!value) return null;

      // Full URL?
      if (/^https?:\/\//i.test(value)) {
        if (value.includes("drive.google.com")) {
          try {
            // https://drive.google.com/file/d/FILE_ID/view
            const match = value.match(/\/file\/d\/([^/]+)/);
            if (match && match[1]) return match[1];

            // https://drive.google.com/open?id=FILE_ID
            const urlObj = new URL(value);
            const idParam = urlObj.searchParams.get("id");
            if (idParam) return idParam;
          } catch (e) {
            return null;
          }
        }
        return null;
      }

      // uc?export=view&id=FILE_ID  or /uc?export=view&id=FILE_ID
      if (value.startsWith("uc?") || value.startsWith("/uc?")) {
        const search = value.startsWith("/") ? value.slice(1) : value;
        const parts = search.split("?");
        const query = parts.length > 1 ? parts[1] : parts[0];
        const params = new URLSearchParams(query);
        const id = params.get("id");
        if (id) return id;
      }

      // id=FILE_ID
      if (value.startsWith("id=")) {
        return value.substring(3).split("&")[0];
      }

      // Plain FILE_ID
      if (/^[A-Za-z0-9_-]{20,}$/.test(value)) {
        return value;
      }

      return null;
    };

    const driveId = extractDriveId(trimmed);
    if (driveId) {
      // backend proxy (prevents Drive/CORS/preview issues)
      return `${API_BASE_URL}/api/utils/drive-image/?id=${encodeURIComponent(driveId)}`;
    }

    // absolute non-drive url
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    // relative backend media path
    if (trimmed.startsWith("/")) return `${API_BASE_URL}${trimmed}`;

    return trimmed;
  }

  // validate cart quantities
  const cartValidation = useMemo(() => {
    const errors = [];
    for (const x of items) {
      const qty = Number(x.quantity);
      const avail =
        x.available === null || x.available === undefined ? null : Number(x.available);

      if (!qty || qty <= 0) {
        errors.push(`${x.code || `Item #${x.itemId}`}: quantity must be > 0`);
        continue;
      }

      // Only validate against available if we actually have it
      if (avail !== null && !Number.isNaN(avail) && qty > avail) {
        errors.push(
          `${x.code || `Item #${x.itemId}`}: quantity (${qty}) exceeds available (${avail})`
        );
      }
    }
    return errors;
  }, [items]);

  const canSubmit = useMemo(() => {
    if (!items.length) return false;

    if (!borrowDate) return false;
    if (borrowDate < todayISO) return false;

    if (!expectedReturnDate) return false;
    if (expectedReturnDate < todayISO) return false;

    // NEW: return must be after borrow
    if (expectedReturnDate < borrowDate) return false;

    const eventDate = header?.event_date || "";

    if (!eventDate) return false;

    // NEW: event must be within borrow period
    if (eventDate < borrowDate) return false;
    if (eventDate > expectedReturnDate) return false;

    if (cartValidation.length) return false;

    return true;
  }, [items, borrowDate, expectedReturnDate, header?.event_date, cartValidation, todayISO]);

  const handleSubmit = async () => {
    const eventName = (header?.event_name || "").trim();
    const eventPicName = (header?.event_pic_name || "").trim();
    const eventDate = header?.event_date || "";
    setError("");

    if (!token) {
      setError("You must be logged in to submit a borrow request.");
      return;
    }

    if (!borrowDate) {
      setError("Borrow date is required.");
      return;
    }
    if (borrowDate < todayISO) {
      setError("Borrow date cannot be in the past.");
      return;
    }

    if (!eventName) return setError("Event name is required.");
    if (!eventPicName) return setError("PIC event name is required.");
    if (!eventDate) return setError("Event date is required.");
    if (eventDate < todayISO) return setError("Event date cannot be in the past.");

    if (expectedReturnDate && expectedReturnDate < borrowDate) {
      setError("Expected return date cannot be before borrow date.");
      return;
    }

    if (eventDate && eventDate < borrowDate) {
      setError("Event date cannot be before borrow date.");
      return;
    }
    if (eventDate && expectedReturnDate && eventDate > expectedReturnDate) {
      setError("Event date cannot be after expected return date.");
      return;
    }

    if (!expectedReturnDate) {
      setError("Expected return date is required.");
      return;
    }
    if (expectedReturnDate < todayISO) {
      setError("Expected return date cannot be in the past.");
      return;
    }
    if (!items.length) {
      setError("Cart is empty.");
      return;
    }
    if (cartValidation.length) {
      setError(cartValidation[0]); // show first error (clean)
      return;
    }

    const payload = {
      expected_return_date: expectedReturnDate,
      purpose_notes: purposeNotes || "",

      event_name: eventName,
      event_pic_name: eventPicName,
      event_date: eventDate,
      borrow_date: borrowDate,
      
      items: items.map((x) => ({
        item_id: x.itemId,
        quantity: Number(x.quantity),
      })),
    };

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/borrow-batches/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.stock?.detail) {
          setError(data.stock.detail);
          return;
        }

        if (data?.detail) {
          setError(data.detail);
          return;
        }

        if (typeof data === "string" && data.trim()) {
          setError(data);
          return;
        }

        setError("Failed to submit borrow batches.");
        return;
      }

      clear();
      navigate("/my-transactions");
    } catch (e) {
      console.error(e);
      setError("Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Zoom modal */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewImageUrl(null)}
        >
          <div
            className="glass-card-xl max-w-3xl w-full p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-semibold text-strong">Image preview</div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-brand-500/5"
                onClick={() => setPreviewImageUrl(null)}
              >
                Close
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src={previewImageUrl}
                alt="Preview"
                className="max-h-[70vh] w-auto object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Borrow Cart</h1>
          <p className="text-xs text-muted mt-1">
            Submit once. System auto-splits into batches of max 10 items.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/items")}
          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md border border-white/50 dark:border-white/15 text-strong hover:bg-brand-500/5"
        >
          Back to Items
        </button>
      </div>

      {items.length === 0 ? (
        <div className="glass-card p-4 text-sm text-muted">
          Cart is empty.
        </div>
      ) : (
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-muted">
                  Item
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-muted w-32">
                  Qty
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-muted w-32">
                  Available
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-muted w-24">
                  Remove
                </th>
              </tr>
            </thead>

            <tbody>
              {items.map((x) => {
                const avail =
                  x.available === null || x.available === undefined ? null : Number(x.available);
                const qty = Number(x.quantity);

                const rowOver =
                  avail !== null && !Number.isNaN(avail) && qty > 0 && qty > avail;

                // IMPORTANT: cart items might carry image in either key
                const imgRaw = x.image || x.image_url || null;
                const img = normalizeImageUrl(imgRaw);

                return (
                  <tr key={x.itemId} className="border-t">
                    <td className="px-4 py-2 text-xs text-strong">
                      <div className="flex items-center gap-3">
                        {img ? (
                          <button
                            type="button"
                            className="shrink-0"
                            onClick={() => setPreviewImageUrl(img)}
                            title="Click to zoom"
                          >
                            <img
                              src={img}
                              alt={x.name || `Item #${x.itemId}`}
                              className="h-10 w-10 object-cover rounded border border-white/40 dark:border-white/10"
                              onError={(e) => {
                                // hide broken thumbnails
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </button>
                        ) : (
                          <div className="h-10 w-10 rounded border border-white/40 dark:border-white/10 bg-brand-500/5 shrink-0" />
                        )}

                        <div>
                          <div className="text-xs text-strong">
                            {x.code ? <span className="font-semibold mr-1">{x.code}</span> : null}
                            {x.name || `Item #${x.itemId}`}
                          </div>
                          <div className="text-[11px] text-muted">
                            {avail === null || Number.isNaN(avail) ? "Available: -" : `Available: ${avail}`}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        value={x.quantity === "" ? "" : x.quantity}
                        onChange={(e) => {
                          const raw = e.target.value;

                          // allow empty while typing (do not auto-remove)
                          if (raw === "") {
                            setQuantity(x.itemId, "");
                            return;
                          }

                          const value = Number(raw);

                          // hard guard if available is known
                          if (
                            avail !== null &&
                            !Number.isNaN(avail) &&
                            value > avail
                          ) {
                            setError(
                              `${x.code || `Item #${x.itemId}`}: quantity exceeds available (${avail}).`
                            );
                            // still set it so user sees what they typed; they must fix it
                            setQuantity(x.itemId, value);
                            return;
                          }

                          setError("");
                          setQuantity(x.itemId, value);
                        }}
                        className={
                          "w-24 border rounded-md px-2 py-1 text-xs text-right " +
                          (rowOver ? "border-red-400" : "border-white/50 dark:border-white/15")
                        }
                      />
                      {x.unit ? (
                        <span className="text-[11px] text-muted ml-2">{x.unit}</span>
                      ) : null}

                      {rowOver ? (
                        <div className="text-[11px] text-red-600 mt-1">Exceeds available</div>
                      ) : null}
                    </td>

                    <td className="px-4 py-2 text-right text-[11px] text-muted">
                      {avail === null || Number.isNaN(avail) ? "-" : avail}
                    </td>

                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeItem(x.itemId)}
                        className="inline-flex items-center px-2 py-1 rounded-md bg-red-600 text-white text-[11px] font-semibold"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {cartValidation.length > 0 ? (
            <div className="px-4 py-3 text-xs border-t border-[var(--border-subtle)] bg-[var(--danger-subtle)] text-[var(--danger)]">
              • {cartValidation[0]}
            </div>
          ) : null}
        </div>
      )}

      {/* batch-level fields */}
      <div className="mt-4 glass-card p-4 max-w-xl space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Tanggal Peminjaman *
            </label>
            <input
              type="date"
              value={borrowDate}
              min={todayISO}
              onChange={(e) => setBorrowDate(e.target.value)}
              className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted mb-1">
              Expected return date *
            </label>
            <input
              type="date"
              value={expectedReturnDate}
              min={borrowDate || todayISO}
              onChange={(e) => {
                const value = e.target.value;

                if (value && value < todayISO) {
                  setError("Expected return date cannot be in the past.");
                  setExpectedReturnDate("");
                  return;
                }

                setError("");
                setExpectedReturnDate(value);
              }}
              className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
            />
          </div>          
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-muted mb-1">
              Event Name *
            </label>
            <input
              type="text"
              value={header.event_name}
              onChange={(e) => setHeaderField("event_name", e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. Seminar K3"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">
              PIC Event Name *
            </label>
            <input
              type="text"
              value={header.event_pic_name}
              onChange={(e) => setHeaderField("event_pic_name", e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g. Budi"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">
              Event Date *
            </label>
            <input
              type="date"
              value={header.event_date}
              min={borrowDate || todayISO}
              max={expectedReturnDate || undefined}
              onChange={(e) => {
                const value = e.target.value;
                if (value && value < todayISO) {
                  setError("Event date cannot be in the past.");
                  return;
                }
                setHeaderField("event_date", value);
              }}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-muted mb-1">
            Purpose / note (optional)
          </label>
          <textarea
            rows={3}
            value={purposeNotes}
            onChange={(e) => setPurposeNotes(e.target.value)}
            className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
            placeholder="e.g. For event X"
          />
        </div>

        {error ? (
          <div className="note-danger">
            {error}
          </div>
        ) : null}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit request"}
          </button>

          <button
            type="button"
            disabled={submitting || items.length === 0}
            onClick={clear}
            className="inline-flex items-center px-3 py-2 text-xs font-semibold rounded-md border border-white/50 dark:border-white/15 text-strong hover:bg-brand-500/5 disabled:opacity-60"
          >
            Clear cart
          </button>
        </div>
      </div>
    </div>
  );
}
